"use client";

import { useEffect, useState } from "react";
import Link from "next/link";
import AppImage from "@/components/ui/AppImage";
import { trackQuoteRequest } from "@/lib/analytics";

const heroStats = [
{ value: "< 2hr", label: "Response Time" },
{ value: "98%", label: "Guest Satisfaction" },
{ value: "White Glove", label: "Service Standard" },
{ value: "Full-Service", label: "Experience Management" }];


export default function HeroSection() {
  // Initialize visible so the hero (incl. the H1 — the LCP element) is painted in the
  // server HTML instead of waiting for client hydration to flip opacity-0 -> opacity-100.
  // That hydration gate was the main cause of the ~9.6s mobile LCP. Trade-off: the JS
  // entrance fade no longer plays; content appears immediately.
  const [mounted, setMounted] = useState(true);

  useEffect(() => {
    setMounted(true);
  }, []);

  return (
    <section className="relative min-h-screen hero-bg overflow-hidden flex flex-col">
      {/* Background image with overlay */}
      <div className="absolute inset-0 z-0">
        <AppImage
          src="https://img.rocket.new/generatedImages/rocket_gen_img_19317fae5-1772226464458.png"
          alt="Luxury beachfront vacation rental property at sunset in Panama City Beach Florida"
          fill
          className="object-cover opacity-30"
          priority />
        
        <div className="absolute inset-0 bg-gradient-to-b from-navy-dark/60 via-navy/40 to-navy-dark/80" />
        {/* Subtle grid overlay */}
        <div
          className="absolute inset-0 opacity-5"
          style={{
            backgroundImage: `linear-gradient(rgba(201,168,76,0.3) 1px, transparent 1px), linear-gradient(90deg, rgba(201,168,76,0.3) 1px, transparent 1px)`,
            backgroundSize: "60px 60px"
          }} />
        
      </div>
      {/* Floating decorative orbs */}
      <div className="absolute top-24 right-16 w-72 h-72 rounded-full bg-gold/5 blur-3xl animate-float pointer-events-none" />
      <div className="absolute bottom-32 left-8 w-96 h-96 rounded-full bg-blue-400/5 blur-3xl animate-float-delayed pointer-events-none" />
      {/* Main content */}
      <div className="relative z-10 flex-1 flex flex-col justify-center pt-24 pb-12">
        <div className="max-w-7xl mx-auto px-5 sm:px-8 lg:px-12 w-full">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 items-center">
            {/* Left: Text */}
            <div className="lg:col-span-7 flex flex-col">
              {/* Badge */}
              <div
                className={`inline-flex items-center gap-2.5 self-start mb-7 px-4 py-2 rounded-full glass border border-gold/30 transition-all duration-700 ${
                mounted ? "opacity-100 translate-y-0" : "opacity-0 translate-y-4"}`
                }>
                
                <span className="w-2 h-2 rounded-full bg-gold animate-pulse" />
                <span className="text-gold text-xs font-semibold tracking-widest uppercase">
                  Panama City Beach, FL
                </span>
                <span className="text-white/40 text-xs">•</span>
                <span className="text-white/60 text-xs">Accepting New Clients</span>
              </div>

              {/* Headline */}
              <h1
                className={`font-heading text-5xl sm:text-6xl lg:text-7xl text-white leading-[1.05] mb-6 transition-all duration-700 delay-150 ${
                mounted ? "opacity-100 translate-y-0" : "opacity-0 translate-y-6"}`
                }>
                
                <span className="block">Luxury Cleaning</span>
                <span className="block text-gradient-gold italic font-light">
                  & Full-Service
                </span>
                <span className="block">Management</span>
              </h1>

              {/* Sub */}
              <p
                className={`text-white/70 text-lg sm:text-xl leading-relaxed max-w-xl mb-10 transition-all duration-700 delay-300 ${
                mounted ? "opacity-100 translate-y-0" : "opacity-0 translate-y-4"}`
                }>
                
                We handle everything — from 5-star turnovers to revenue optimization — so you earn more while doing less.
              </p>

              {/* CTAs */}
              <div
                className={`flex flex-col sm:flex-row gap-4 mb-12 transition-all duration-700 delay-500 ${
                mounted ? "opacity-100 translate-y-0" : "opacity-0 translate-y-4"}`
                }>
                
                <Link
                  href="/book"
                  className="btn-gold px-8 py-4 rounded-full font-semibold text-base flex items-center justify-center gap-2.5 group">

                  Book Now
                  <svg className="w-4 h-4 group-hover:translate-x-1 transition-transform" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 8l4 4m0 0l-4 4m4-4H3" />
                  </svg>
                </Link>
                <Link
                  href="/contact"
                  className="btn-ghost px-8 py-4 rounded-full font-medium text-base flex items-center justify-center gap-2">
                  
                  Become a Client
                </Link>
              </div>

              {/* Trust indicators */}
              <div
                className={`flex flex-wrap items-center gap-6 transition-all duration-700 delay-700 ${
                mounted ? "opacity-100 translate-y-0" : "opacity-0 translate-y-4"}`
                }>
                
                <span className="text-white/60 text-sm">Licensed & Insured</span>
              </div>
            </div>

            {/* Right: Floating cards */}
            <div className="lg:col-span-5 relative h-[460px] hidden lg:flex items-center justify-center">
              {/* Main property card */}
              <div
                className={`absolute top-0 right-0 w-72 glass-light rounded-3xl overflow-hidden shadow-luxury-lg animate-float transition-all duration-700 delay-300 ${
                mounted ? "opacity-100 scale-100" : "opacity-0 scale-95"}`
                }>
                
                <div className="h-44 overflow-hidden">
                  <AppImage
                    src="https://img.rocket.new/generatedImages/rocket_gen_img_1e825c6f8-1764888787767.png"
                    alt="Luxury beachfront condo interior with ocean view"
                    width={288}
                    height={176}
                    className="w-full h-full object-cover" />
                  
                </div>
                <div className="p-4">
                  <div className="flex items-center justify-between mb-1">
                    <span className="text-navy font-semibold text-sm">Beachfront Condo</span>
                    <div className="flex items-center gap-1">
                      <svg className="w-3.5 h-3.5 text-gold fill-gold" viewBox="0 0 20 20">
                        <path d="M9.049 2.927c.3-.921 1.603-.921 1.902 0l1.07 3.292a1 1 0 00.95.69h3.462c.969 0 1.371 1.24.588 1.81l-2.8 2.034a1 1 0 00-.364 1.118l1.07 3.292c.3.921-.755 1.688-1.54 1.118l-2.8-2.034a1 1 0 00-1.175 0l-2.8 2.034c-.784.57-1.838-.197-1.539-1.118l1.07-3.292a1 1 0 00-.364-1.118L2.98 8.72c-.783-.57-.38-1.81.588-1.81h3.461a1 1 0 00.951-.69l1.07-3.292z" />
                      </svg>
                      <span className="text-navy text-xs font-medium">4.98</span>
                    </div>
                  </div>
                  <p className="text-navy/50 text-xs">PCB, FL · 2BR · Managed by Premier</p>
                </div>
              </div>

              {/* Revenue card */}
              <div
                className={`absolute bottom-12 left-0 w-56 glass-dark rounded-2xl p-4 shadow-luxury animate-float-delayed transition-all duration-700 delay-500 ${
                mounted ? "opacity-100 scale-100" : "opacity-0 scale-95"}`
                }>
                
                <div className="flex items-center gap-2 mb-3">
                  <div className="w-7 h-7 rounded-lg bg-gold/20 flex items-center justify-center">
                    <svg className="w-4 h-4 text-gold" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M12 8c-1.657 0-3 .895-3 2s1.343 2 3 2 3 .895 3 2-1.343 2-3 2m0-8c1.11 0 2.08.402 2.599 1M12 8V7m0 1v8m0 0v1m0-1c-1.11 0-2.08-.402-2.599-1M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                    </svg>
                  </div>
                  <span className="text-white/70 text-xs font-medium">Monthly Revenue</span>
                </div>
                <p className="text-gold font-heading text-2xl font-bold mb-0.5">$6,840</p>
                <div className="flex items-center gap-1">
                  <svg className="w-3 h-3 text-emerald-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 10l7-7m0 0l7 7m-7-7v18" />
                  </svg>
                  <span className="text-emerald-400 text-xs font-medium">+24% vs last month</span>
                </div>
              </div>

              {/* Clean notification */}
              <div
                className={`absolute top-1/2 left-4 w-52 glass rounded-xl p-3.5 shadow-lg transition-all duration-700 delay-700 ${
                mounted ? "opacity-100 translate-x-0" : "opacity-0 -translate-x-4"}`
                }>
                
                <div className="flex items-center gap-3">
                  <div className="w-8 h-8 rounded-full bg-emerald-500/20 flex items-center justify-center shrink-0">
                    <svg className="w-4 h-4 text-emerald-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                    </svg>
                  </div>
                  <div>
                    <p className="text-white text-xs font-semibold">Property Ready</p>
                    <p className="text-white/50 text-xs">5-star turnover complete</p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
      {/* Stats bar */}
      <div className="relative z-10 border-t border-white/10">
        <div className="max-w-7xl mx-auto px-5 sm:px-8 lg:px-12">
          <div className="grid grid-cols-2 lg:grid-cols-4 divide-x divide-white/10">
            {heroStats?.map((stat, i) =>
            <div
              key={stat?.label}
              className={`py-6 px-6 text-center transition-all duration-700 ${
              mounted ? "opacity-100 translate-y-0" : "opacity-0 translate-y-4"}`
              }
              style={{ transitionDelay: `${600 + i * 100}ms` }}>
              
                <div className="text-gold font-heading text-2xl sm:text-3xl font-bold mb-1">{stat?.value}</div>
                <div className="text-white/50 text-xs uppercase tracking-wider">{stat?.label}</div>
              </div>
            )}
          </div>
        </div>
      </div>
      {/* Scroll indicator */}
      <div className="absolute bottom-8 left-1/2 -translate-x-1/2 z-10 hidden md:flex flex-col items-center gap-2 animate-float">
        <span className="text-white/30 text-xs uppercase tracking-widest">Scroll</span>
        <div className="w-0.5 h-8 bg-gradient-to-b from-white/30 to-transparent" />
      </div>
    </section>);

}